import React, {ReactElement, useState} from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {Button, InfoCircleIcon, Tooltip} from '@customink/pigment-react';
import {StoreDetails} from 'models/storeDetails';
import {StoreProduct} from '../../models/storeProduct';
import ArrowUpIcon from '../../assets/icons/arrow_up.svg';
import ArrowDownIcon from '../../assets/icons/arrow_down.svg';
import MoveIcon from '../../assets/icons/drag.svg';
import {
  useUpdateStoreProductMutation,
  useReorderStoreProductsMutation,
  StoreProductReorderParamsType,
  StoreProductUpdateParamsType
} from '../../api/storesApi';
import {Product} from '../../models/product';
import ProductColorSwatch from '../ProductColorSwatch';
import DeletionDialog from '../DeletionDialog';
import {useStoreType} from '../../hooks/useStoreType';
import {getProductColors, formatCurrency} from '../../utils/helpers';

type StoreProductsListProps = {
  products: StoreProduct[];
  mmsProducts: Product[];
  storeDetails: StoreDetails;
  storeSlug: string;
  setAddedProductsQty: (quantity: string | null) => void;
};

function StoreProductRow(props: {
  product: StoreProduct;
  index: number;
  disableProduct: (product: StoreProduct) => void;
  mmsProducts: Product[];
  deleteEnabled: boolean;
  deleteProductsEnabled: boolean;
  setAddedProductsQty: (quantity: string | null) => void;
}) {
  const {
    product,
    index,
    disableProduct,
    mmsProducts,
    deleteEnabled,
    deleteProductsEnabled,
    setAddedProductsQty
  } = props;
  const [open, setOpen] = useState(false);

  return (
    <Draggable key={product.id} draggableId={String(product.id)} index={index}>
      {(provided, snapshot) => (
        <TableRow
          className={`productRow${snapshot.isDragging ? ' dragging' : ''}`}
          ref={provided.innerRef}
          {...provided.draggableProps}>
          <TableCell className="productTableCell dragTableCell">
            <div className="stringField">
              <img src={MoveIcon} alt="Move" {...provided.dragHandleProps} />
            </div>
          </TableCell>
          <TableCell className="productTableCell">
            <div className="productInfo">
              <div className="productImageContainer">
                {product.imageUrl && (
                  <img src={product.imageUrl} alt="custom design" />
                )}
              </div>
              <div className="productInfoItem">
                <span>{product.name}</span>
              </div>
              <div className="productInfoItem">
                <IconButton
                  aria-label={open ? 'Collapse row' : 'Expand row'}
                  className="collapseButton"
                  onClick={() => setOpen(!open)}>
                  {open ? (
                    <img src={ArrowUpIcon} alt="Collapse" />
                  ) : (
                    <img src={ArrowDownIcon} alt="Expand" />
                  )}
                </IconButton>
              </div>
            </div>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box className="productColorSwatches">
                <h4>Product Colors:</h4>
                {mmsProducts &&
                  getProductColors(product, mmsProducts).map(c => (
                    <ProductColorSwatch
                      rgb={c.rgbs[0]}
                      productId={null}
                      trimRgb={c.trim_rgb}
                      colorName={c.name}
                      colorId={c.id}
                      key={c.name}
                      handleSelect={null}
                    />
                  ))}
                {!mmsProducts && (
                  <Skeleton variant="rectangular" width={27} height={27} />
                )}
              </Box>
            </Collapse>
          </TableCell>
          <TableCell className="productTableCell">
            <div className="currencyField">{formatCurrency(product.price)}</div>
          </TableCell>

          {deleteEnabled ? (
            <TableCell align="left" className="productTableCell delete">
              <div className="stringField">
                <DeletionDialog
                  product={product}
                  disableProduct={disableProduct}
                  deleteProductsEnabled={deleteProductsEnabled}
                  setAddedProductsQty={setAddedProductsQty}
                />
              </div>
            </TableCell>
          ) : null}
        </TableRow>
      )}
    </Draggable>
  );
}

const StoreProductList: React.FC<StoreProductsListProps> = ({
  products,
  mmsProducts,
  storeDetails,
  storeSlug,
  setAddedProductsQty
}: StoreProductsListProps): ReactElement => {
  const [triggerUpdateStoreProductMutation] = useUpdateStoreProductMutation();
  const [triggerReorderStoreProductsMutation] =
    useReorderStoreProductsMutation();
  const [productOrderTouched, setProductOrderTouched] = useState(false);

  const {isStoreTypeSelfServe} = useStoreType();

  const deleteProductsEnabled =
    storeDetails?.deleteProductsEnabled && storeDetails?.eventStore;

  const defaultProductOrder = () => {
    return products.map(p => p.id);
  };

  const [productOrder, setProductOrder] = useState(defaultProductOrder());

  const discardChanges = () => {
    setProductOrder(defaultProductOrder());
    setProductOrderTouched(false);
  };

  const disableProduct = (product: StoreProduct) => {
    const storeProductParams: StoreProductUpdateParamsType = {
      url_slug: storeSlug,
      product_id: product.id,
      product: {
        enabled: false
      }
    };
    triggerUpdateStoreProductMutation(storeProductParams);
    // this needs to depend on the update actually succeeding
    setProductOrder(productOrder.filter(item => item !== product.id));
  };

  const reorderProducts = () => {
    const params: StoreProductReorderParamsType = {
      url_slug: storeSlug,
      product_ids: productOrder
    };
    triggerReorderStoreProductsMutation(params);
    setProductOrderTouched(false);
  };

  const onDragEnd = (result: any) => {
    const {destination, source} = result;
    if (destination && destination.index !== source.index) {
      const [removed] = productOrder.splice(source.index, 1);
      productOrder.splice(destination.index, 0, removed);
      setProductOrder(productOrder);
      setProductOrderTouched(true);
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        {products && (
          <Droppable droppableId="productsList">
            {provided => (
              <TableContainer
                component={Paper}
                className="productsList-container"
                ref={provided.innerRef}
                {...provided.droppableProps}>
                <Table aria-label="collapsible table">
                  <TableHead>
                    <TableRow sx={{fontWeight: 'bold'}}>
                      <TableCell />
                      <TableCell align="left">Product</TableCell>
                      <TableCell align="right">Sale Price</TableCell>
                      {isStoreTypeSelfServe || deleteProductsEnabled ? (
                        <TableCell>
                          <div className="deleteHeader">
                            Delete
                            {deleteProductsEnabled && (
                              <Tooltip
                                title="Products that have been purchased cannot be removed."
                                arrow
                                placement="top-start">
                                <IconButton>
                                  <InfoCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productOrder.map((productId, index) => (
                      <StoreProductRow
                        key={productId}
                        index={index}
                        product={products.find(p => p.id === productId)}
                        disableProduct={disableProduct}
                        mmsProducts={mmsProducts}
                        deleteEnabled={
                          isStoreTypeSelfServe || deleteProductsEnabled
                        }
                        deleteProductsEnabled={deleteProductsEnabled}
                        setAddedProductsQty={setAddedProductsQty}
                      />
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Droppable>
        )}
      </DragDropContext>
      {productOrderTouched && (
        <div className="applyChangesBar">
          <div>You have unsaved changes.</div>
          <div className="actions">
            <Button
              type="submit"
              className="saveButton"
              onClick={reorderProducts}>
              Save Changes
            </Button>
            <Button type="reset" variant="secondary" onClick={discardChanges}>
              Discard Changes
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default StoreProductList;
